import React, { useState } from 'react';
import { Search, FileText, CheckCircle, XCircle, Download, FileCheck2, Calendar, FileBadge2, User, HelpCircle } from 'lucide-react';
import api from '../services/api.js';

export default function VerificarCertificado() {
  const [numero, setNumero] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!numero.trim()) return;

    setLoading(true);
    setResult(null);
    setError(null);

    try {
      const response = await api.get(`/verificar/fojas/${numero}`);
      const data = response.data;
      
      if (data.exists) {
        setResult(data.data);
      } else {
        setError(data.message || 'El documento no fue encontrado.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Ocurrió un error al contactar el servidor de fojas.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-body)', display: 'flex', flexDirection: 'column' }}>

      {/* ── Buscador ── */}
      <section style={{ flex: 1, padding: '3rem 1rem' }}>
        <div className="container" style={{ maxWidth: '800px' }}>
          
          <div className="glass-card animate-scale-in" style={{ background: 'var(--bg-surface)', padding: '2rem', marginBottom: '2rem', textAlign: 'center' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', color: 'var(--text-main)' }}>Consulta de Certificado N°</h2>
            
            <form onSubmit={handleVerify} style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
              <div style={{ flex: '1 1 300px', position: 'relative' }}>
                <div style={{ position: 'absolute', top: '50%', left: '1rem', transform: 'translateY(-50%)', color: 'var(--copper)' }}>
                  <Search size={20} />
                </div>
                <input
                  type="text"
                  value={numero}
                  onChange={(e) => setNumero(e.target.value)}
                  placeholder="Ej: 12345678"
                  style={{
                    width: '100%', padding: '1rem 1rem 1rem 3rem',
                    borderRadius: '8px', border: '1px solid var(--border-color)',
                    background: 'var(--bg-body)', color: 'var(--text-main)', fontSize: '1.1rem',
                    transition: 'border-color 0.2s, box-shadow 0.2s', outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--copper)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border-color)'}
                />
              </div>
              <button 
                type="submit" 
                className="btn btn-primary" 
                disabled={loading || !numero.trim()}
                style={{ padding: '0 2rem', fontSize: '1.1rem', height: '54px', display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: '180px', justifyContent: 'center' }}
              >
                {loading ? <div className="spinner" style={{ width: '20px', height: '20px', borderWidth: '3px' }}></div> : 'Verificar'}
              </button>
            </form>
            
            <p style={{ marginTop: '1.5rem', fontSize: '0.85rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
              <HelpCircle size={14} /> El número lo encuentras impreso en la parte superior o pie de página del documento original.
            </p>
          </div>

          {/* ── Resultados ── */}
          {error && (
            <div className="glass-card animate-fade-in" style={{ padding: '2rem', borderLeft: '4px solid #ef4444', background: '#fef2f2', color: '#991b1b' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                <XCircle size={24} color="#dc2626" />
                <h3 style={{ fontSize: '1.25rem', margin: 0, fontWeight: 600 }}>Atención</h3>
              </div>
              <p style={{ margin: 0, paddingLeft: '2.25rem' }}>{error}</p>
            </div>
          )}

          {result && (
            <div className="glass-card animate-fade-in" style={{ padding: '0', overflow: 'hidden', background: 'var(--bg-surface)' }}>
              
              <div style={{ background: 'var(--primary-light)', color: 'white', padding: '1.5rem 2rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <CheckCircle size={32} color="#10b981" />
                <div>
                  <h3 style={{color: 'white', margin: 0, fontSize: '1.4rem', fontWeight: 600 }}>Documento verificado</h3>
                  <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.9 }}>Verificado exitosamente de forma electrónica contra el registro matriz.</p>
                </div>
              </div>

              <div style={{ padding: '2rem' }}>
                <h4 style={{ fontSize: '1.2rem', color: 'var(--text-main)', marginBottom: '1.5rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem' }}>
                  Detalles del Certificado N° {numero}
                </h4>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
                  
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <div style={{ color: 'var(--copper)' }}><FileText size={24} /></div>
                    <div>
                      <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '1px' }}>Tipo de Certificado</div>
                      <div style={{ fontSize: '1.05rem', color: 'var(--text-main)', fontWeight: 500 }}>{result.certificado}</div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <div style={{ color: 'var(--copper)' }}><Calendar size={24} /></div>
                    <div>
                      <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '1px' }}>Fecha Emisión</div>
                      <div style={{ fontSize: '1.05rem', color: 'var(--text-main)', fontWeight: 500 }}>{result.fecha}</div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <div style={{ color: 'var(--copper)' }}><FileBadge2 size={24} /></div>
                    <div>
                      <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '1px' }}>Carátula asociada</div>
                      <div style={{ fontSize: '1.05rem', color: 'var(--text-main)', fontWeight: 500 }}>{result.caratula}</div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <div style={{ color: 'var(--copper)' }}><User size={24} /></div>
                    <div>
                      <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '1px' }}>Entidad Emisora</div>
                      <div style={{ fontSize: '1.05rem', color: 'var(--text-main)', fontWeight: 500 }}>{result.emisor}</div>
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '2.5rem', display: 'flex', justifyContent: 'center' }}>
                  <a 
                    href={result.download_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn"
                    style={{ 
                      background: 'var(--primary)', color: 'white', padding: '1rem 2.5rem', borderRadius: '30px', 
                      display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '1.1rem', fontWeight: 600,
                      boxShadow: '0 4px 15px rgba(0,0,0,0.2)', transition: 'transform 0.2s', textDecoration: 'none'
                    }}
                    onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                    onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    <Download size={24} />
                    Descargar Certificado Original PDF
                  </a>
                </div>

              </div>
            </div>
          )}

        </div>
      </section>
    </div>
  );
}
